import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
  Play, Copy, Download, Settings2, ChevronRight, RotateCcw,
  Anchor, ArrowRightLeft, Save, CheckCircle, Loader2, Eye, EyeOff,
  AlertTriangle
} from 'lucide-react';
import { 
  turningOperations, 
  millingOperations,
  controllerConfig,
  formatGCodeLine 
} from '@/utils/gcode';
import { materialLibrary, calculateRPM, getCuttingParams } from '@/utils/materials';
import { liveToolingOperations, generateLiveToolingCode, turnMillConfig, liveToolingCategories, getAvailableOperations, machinePresets } from '@/utils/turnmill';
import { uncThreads, unfThreads, metricCoarseThreads, metricFineThreads, getThreadList } from '@/utils/threadLibrary';
import MaterialSetup from '@/components/MaterialSetup';
import ToolpathViewer from '@/components/ToolpathViewer';
import ToolpathViewer3D from '@/components/ToolpathViewer3DWrapper';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Common insert geometry presets for quick tool nose comp setup
const INSERT_PRESETS = [
  { code: 'CNMG', label: 'OD Rough 80°', tipAngle: 80, noseRadiusMm: 0.8, noseRadiusIn: 0.031 },
  { code: 'CNMG-12', label: 'OD Rough 80° (1.2mm)', tipAngle: 80, noseRadiusMm: 1.2, noseRadiusIn: 0.047 },
  { code: 'DNMG', label: 'OD Finish 55°', tipAngle: 55, noseRadiusMm: 0.4, noseRadiusIn: 0.016 },
  { code: 'DNMG-08', label: 'OD Finish 55° (0.8mm)', tipAngle: 55, noseRadiusMm: 0.8, noseRadiusIn: 0.031 },
  { code: 'VNMG', label: 'OD Profile 35°', tipAngle: 35, noseRadiusMm: 0.4, noseRadiusIn: 0.016 },
  { code: 'WNMG', label: 'OD General 80°', tipAngle: 80, noseRadiusMm: 0.8, noseRadiusIn: 0.031 },
  { code: 'SNMG', label: 'Facing 90°', tipAngle: 90, noseRadiusMm: 0.8, noseRadiusIn: 0.031 },
  { code: 'TNMG', label: 'OD General 60°', tipAngle: 60, noseRadiusMm: 0.4, noseRadiusIn: 0.016 },
  { code: 'CCMT', label: 'ID Bore 80°', tipAngle: 80, noseRadiusMm: 0.4, noseRadiusIn: 0.016 },
  { code: 'DCMT', label: 'ID Bore 55°', tipAngle: 55, noseRadiusMm: 0.2, noseRadiusIn: 0.008 },
  { code: 'VCMT', label: 'ID Profile 35°', tipAngle: 35, noseRadiusMm: 0.2, noseRadiusIn: 0.008 },
  { code: 'TCMT', label: 'ID General 60°', tipAngle: 60, noseRadiusMm: 0.2, noseRadiusIn: 0.008 },
];

// Workpiece holder configurations
const workpieceHolders = {
  turning: {
    '3-jaw-od': { name: '3-Jaw Chuck (OD)', description: 'External grip on OD', gripType: 'OD' },
    '3-jaw-id': { name: '3-Jaw Chuck (ID)', description: 'Internal grip on bore', gripType: 'ID' },
    '6-jaw-od': { name: '6-Jaw Chuck (OD)', description: 'Better grip for thin walls', gripType: 'OD' },
    'collet': { name: 'Collet Chuck', description: 'High precision, light cuts', gripType: 'OD' },
    'soft-jaws': { name: 'Soft Jaws', description: 'Custom machined for part', gripType: 'OD' },
    'faceplate': { name: 'Faceplate', description: 'For irregular shapes', gripType: 'OD' },
  },
  milling: {
    'vise': { name: 'Machine Vise', description: 'Standard workholding', gripType: 'sides' },
    'vise-soft-jaws': { name: 'Vise w/ Soft Jaws', description: 'Custom jaws for part', gripType: 'sides' },
    'v-block': { name: 'V-Block', description: 'For round stock', gripType: 'OD' },
    'clamps': { name: 'Step Clamps', description: 'Direct clamping', gripType: 'top' },
    't-slot': { name: 'T-Slot Fixture', description: 'Bolt-down fixture', gripType: 'bottom' },
    'vacuum': { name: 'Vacuum Table', description: 'For thin/flat parts', gripType: 'bottom' },
  }
};

// Program Builder Component for multi-operation programs
export const ProgramBuilder = ({ 
  type = 'turning', // 'turning' or 'milling'
  controller = 'fanuc',
  machineConfig = '2axis', // '2axis', '2axis-c', '3axis-y'
  onGenerateCode 
}) => {
  const operations = type === 'turning' ? turningOperations : millingOperations;
  const [programOps, setProgramOps] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState('alu-6061-t6');
  const [stockDimensions, setStockDimensions] = useState({ diameter: 2.0, length: 4.0 });
  const [cuttingParams, setCuttingParams] = useState(null);
  const [programSettings, setProgramSettings] = useState({
    programNum: 1,
    programName: '',
    safeZ: type === 'turning' ? 1.0 : 2.0,
    safeX: type === 'turning' ? 4.0 : 6.0,
    safeY: 6.0,
    useCoolant: true,
    useG50SpindleLimit: true,
    maxSpindle: 3500,
  });
  const [gcode, setGcode] = useState([]);
  const [copied, setCopied] = useState(false);
  const [expandedOp, setExpandedOp] = useState(null);
  const [showLiveTooling, setShowLiveTooling] = useState(false);
  
  // Workpiece holder state
  const [workpieceHolder, setWorkpieceHolder] = useState({
    type: type === 'turning' ? '3-jaw-od' : 'vise',
    jawDiameter: 2.0,       // For turning: grip diameter
    gripLength: 1.0,        // How deep jaws grip
    viseWidth: 4.0,         // For milling: vise jaw opening
    jawHeight: 1.0,         // Height of soft jaws / vise jaws
    parallelHeight: 0.75,   // Parallels under part in vise
  });
  
  // Two-sided machining (flip part) state
  const [flipPart, setFlipPart] = useState({
    enabled: false,
    currentSide: 1,         // 1 or 2
    side1Ops: [],           // Operation IDs for side 1
    side2Ops: [],           // Operation IDs for side 2
    flipStopCode: 'M01',    // M01 (optional) or M00 (mandatory)
    flipComment: 'FLIP PART - RECHUCK AND VERIFY',
  });
  
  // Show setup sections
  const [showHolderSetup, setShowHolderSetup] = useState(false);
  const [showFlipSetup, setShowFlipSetup] = useState(false);
  const [showToolpathViewer, setShowToolpathViewer] = useState(true);
  const [viewMode, setViewMode] = useState('3d'); // '2d' or '3d'
  const [activeMachineConfig, setActiveMachineConfig] = useState(machineConfig);

  // Get calculated speed based on material and diameter
  const getCalculatedSpeed = (material, diameter, operation = 'roughing') => {
    const mat = materialLibrary[material];
    if (!mat) return 1200;
    const params = type === 'turning' ? mat.turning : mat.milling;
    if (!params || !params[operation]) return 1200;
    const avgSFM = (params[operation].sfm[0] + params[operation].sfm[1]) / 2;
    return calculateRPM(avgSFM, diameter);
  };

  // Get calculated feed based on material
  const getCalculatedFeed = (material, operation = 'roughing') => {
    const mat = materialLibrary[material];
    if (!mat) return type === 'turning' ? 0.008 : 12.0;
    const params = type === 'turning' ? mat.turning : mat.milling;
    if (!params || !params[operation]) return type === 'turning' ? 0.008 : 12.0;
    const feedRange = type === 'turning' ? params[operation].ipr : params[operation].ipt;
    return (feedRange[0] + feedRange[1]) / 2;
  };

  // Add a new operation
  const addOperation = (opKey, isLiveTool = false) => {
    const opList = isLiveTool ? liveToolingOperations : operations;
    const op = opList[opKey];
    if (!op) return;
    
    const defaults = {};
    op.fields.forEach(field => {
      defaults[field.id] = field.default;
    });

    // Calculate speed and feed from material
    const calcSpeed = getCalculatedSpeed(selectedMaterial, stockDimensions.diameter || 2.0, 'roughing');
    const calcFeed = getCalculatedFeed(selectedMaterial, 'roughing');
    
    const newOp = {
      id: Date.now(),
      type: opKey,
      name: op.name,
      params: defaults,
      tool: programOps.length + 1,
      toolDesc: op.name.toUpperCase(),
      toolNoseRadius: 0.031,
      speed: Math.min(calcSpeed, programSettings.maxSpindle),
      feed: calcFeed,
      enabled: true,
      isLiveTool: isLiveTool,
      side: flipPart.enabled ? flipPart.currentSide : 1,
      spindleDir: 'M03',
      edgeBreak: {
        entry: { type: 'none', size: 0.015 },
        exit: { type: 'none', size: 0.015 },
      },
      profileSteps: (opKey === 'od-roughing' || opKey === 'id-boring') ? [] : null,
    };
    
    setProgramOps([...programOps, newOp]);
    
    // Update flip part tracking
    if (flipPart.enabled) {
      if (flipPart.currentSide === 1) {
        setFlipPart(fp => ({ ...fp, side1Ops: [...fp.side1Ops, newOp.id] }));
      } else {
        setFlipPart(fp => ({ ...fp, side2Ops: [...fp.side2Ops, newOp.id] }));
      }
    }
  };

  // Remove an operation
  const removeOperation = (id) => {
    setProgramOps(programOps.filter(op => op.id !== id));
    if (expandedOp === id) setExpandedOp(null);
    
    // Update flip part tracking
    setFlipPart(fp => ({
      ...fp,
      side1Ops: fp.side1Ops.filter(opId => opId !== id),
      side2Ops: fp.side2Ops.filter(opId => opId !== id),
    }));
  };

  // Validate machining sequence - check for missing operations
  const [sequenceWarnings, setSequenceWarnings] = useState([]);
  
  const validateMachiningSequence = (ops) => {
    const warnings = [];
    const enabledOps = ops.filter(op => op.enabled);
    
    // Check for boring without prior drilling
    const hasBoring = enabledOps.some(op => 
      op.type === 'id-boring' || op.type === 'id-roughing' || op.type === 'boring'
    );
    const hasCenterDrill = enabledOps.some(op => 
      op.type === 'center-drill' || op.type === 'drilling' && op.params?.drillType === 'center'
    );
    const hasDrilling = enabledOps.some(op => 
      (op.type === 'drilling' || op.type === 'drill') && op.params?.drillType !== 'center'
    );
    
    if (hasBoring) {
      // Get boring operation details
      const boringOp = enabledOps.find(op => 
        op.type === 'id-boring' || op.type === 'id-roughing' || op.type === 'boring'
      );
      const boreDiameter = boringOp?.params?.boreDia || boringOp?.params?.finishDia || 0.5;
      
      // Check sequence: need center drill and pilot drill before boring
      if (!hasCenterDrill && !hasDrilling) {
        warnings.push({
          type: 'error',
          message: `ID Boring requires hole preparation: Add Center Drill + Pilot Drill before boring`,
          suggestedOps: ['center-drill', 'drilling'],
          boreDiameter: boreDiameter
        });
      } else if (hasCenterDrill && !hasDrilling) {
        // Calculate recommended drill size (typically 80-90% of bore diameter for rough bore)
        const recommendedDrillSize = Math.round(boreDiameter * 0.85 * 1000) / 1000;
        warnings.push({
          type: 'error',
          message: `Missing Pilot Drill: Add drilling operation (recommend ${recommendedDrillSize}" drill) after center drill and before ID boring`,
          suggestedOps: ['drilling'],
          recommendedDrillSize: recommendedDrillSize,
          boreDiameter: boreDiameter
        });
      }
      
      // Check operation order
      if (hasCenterDrill && hasDrilling && hasBoring) {
        const centerDrillIndex = enabledOps.findIndex(op => 
          op.type === 'center-drill' || (op.type === 'drilling' && op.params?.drillType === 'center')
        );
        const drillingIndex = enabledOps.findIndex(op => 
          (op.type === 'drilling' || op.type === 'drill') && op.params?.drillType !== 'center'
        );
        const boringIndex = enabledOps.findIndex(op => 
          op.type === 'id-boring' || op.type === 'id-roughing' || op.type === 'boring'
        );
        
        if (centerDrillIndex > drillingIndex) {
          warnings.push({
            type: 'warning',
            message: 'Center Drill should come BEFORE Pilot Drill - reorder operations',
            suggestedOps: []
          });
        }
        if (drillingIndex > boringIndex) {
          warnings.push({
            type: 'warning', 
            message: 'Pilot Drill should come BEFORE Boring - reorder operations',
            suggestedOps: []
          });
        }
      }
    }
    
    // Check for tapping without drilling
    const hasTapping = enabledOps.some(op => op.type === 'tapping' || op.type === 'tap');
    if (hasTapping && !hasDrilling) {
      const tapOp = enabledOps.find(op => op.type === 'tapping' || op.type === 'tap');
      const tapSize = tapOp?.params?.tapSize || 0.25;
      // Calculate tap drill size (tap size - pitch for approx 75% thread)
      const tapDrillSize = Math.round((tapSize - 0.04) * 1000) / 1000;
      warnings.push({
        type: 'error',
        message: `Tapping requires tap drill: Add drilling operation (${tapDrillSize}" tap drill) before tapping`,
        suggestedOps: ['center-drill', 'drilling'],
        recommendedDrillSize: tapDrillSize
      });
    }
    
    // Check for parting operation position (should be last)
    const partingOp = enabledOps.find(op => op.type === 'parting');
    if (partingOp) {
      const partingIndex = enabledOps.indexOf(partingOp);
      const barPullerAfterParting = enabledOps.slice(partingIndex + 1).some(op => op.type !== 'bar-puller');
      if (barPullerAfterParting) {
        warnings.push({
          type: 'warning',
          message: 'Parting should typically be the last cutting operation (before bar puller)',
          suggestedOps: []
        });
      }
    }
    
    return warnings;
  };

  // Run validation whenever operations change
  React.useEffect(() => {
    const warnings = validateMachiningSequence(programOps);
    setSequenceWarnings(warnings);
  }, [programOps]);

  // Auto-add suggested operations
  const addSuggestedOperations = (suggestedOps, insertBeforeType, params = {}) => {
    let newOps = [...programOps];
    const insertIndex = newOps.findIndex(op => 
      op.type === insertBeforeType || op.type === 'id-boring' || op.type === 'id-roughing' || op.type === 'boring'
    );
    
    suggestedOps.forEach((opKey, i) => {
      const op = operations[opKey];
      if (!op) return;
      
      const defaults = {};
      op.fields.forEach(field => {
        defaults[field.id] = field.default;
      });
      
      // Set recommended drill size if provided
      if (opKey === 'drilling' && params.recommendedDrillSize) {
        defaults.drillDia = params.recommendedDrillSize;
        defaults.depth = params.boreDiameter ? params.boreDiameter + 0.5 : 1.0;
      }
      
      const calcSpeed = getCalculatedSpeed(selectedMaterial, stockDimensions.diameter || 2.0, 'roughing');
      const calcFeed = getCalculatedFeed(selectedMaterial, 'roughing');
      
      const newOp = {
        id: Date.now() + i,
        type: opKey,
        name: op.name,
        params: defaults,
        tool: newOps.length + 1 + i,
        toolDesc: op.name.toUpperCase(),
        toolNoseRadius: 0.031,
        speed: Math.min(calcSpeed, programSettings.maxSpindle),
        feed: calcFeed,
        enabled: true,
        isLiveTool: false,
        side: flipPart.enabled ? flipPart.currentSide : 1,
        spindleDir: 'M03',
        edgeBreak: { entry: { type: 'none', size: 0.015 }, exit: { type: 'none', size: 0.015 } },
        profileSteps: (opKey === 'od-roughing' || opKey === 'id-boring') ? [] : null,
      };
      
      if (insertIndex >= 0) {
        newOps.splice(insertIndex + i, 0, newOp);
      } else {
        newOps.push(newOp);
      }
    });
    
    setProgramOps(newOps);
  };

  // Move operation up/down
  const moveOperation = (index, direction) => {
    const newOps = [...programOps];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newOps.length) return;
    [newOps[index], newOps[newIndex]] = [newOps[newIndex], newOps[index]];
    setProgramOps(newOps);
  };

  // Update operation parameters
  const updateOpParam = (id, paramId, value) => {
    setProgramOps(programOps.map(op => {
      if (op.id === id) {
        // String fields for threading
        const stringFields = ['threadSystem', 'threadSeries', 'threadDesignation', 'threadMethod'];
        const newValue = stringFields.includes(paramId) ? value : (parseFloat(value) || 0);
        const newParams = { ...op.params, [paramId]: newValue };
        
        // Auto-populate thread data when designation changes
        if (paramId === 'threadDesignation' && value) {
          const system = op.params.threadSystem || 'imperial';
          const series = op.params.threadSeries || 'UNC';
          const threads = getThreadList(system, series);
          const thread = threads.find(t => t.designation === value);
          if (thread) {
            if (system === 'imperial') {
              newParams.majorDia = thread.majorDia;
              newParams.minorDia = thread.minorDiaMale;
              newParams.pitch = thread.pitch;
              newParams.tpi = thread.tpi;
            } else {
              // Metric: convert mm to inches for G-code
              newParams.majorDia = parseFloat((thread.majorDia / 25.4).toFixed(5));
              newParams.minorDia = parseFloat((thread.minorDiaMale / 25.4).toFixed(5));
              newParams.pitch = parseFloat((thread.pitch / 25.4).toFixed(6));
              newParams.tpi = parseFloat((25.4 / thread.pitch).toFixed(1));
            }
          }
        }
        
        // Reset series and designation when system changes
        if (paramId === 'threadSystem') {
          newParams.threadSeries = value === 'imperial' ? 'UNC' : 'coarse';
          newParams.threadDesignation = '';
        }
        
        // Reset designation when series changes
        if (paramId === 'threadSeries') {
          newParams.threadDesignation = '';
        }
        
        return { ...op, params: newParams };
      }
      return op;
    }));
  };

  // Update operation settings (tool, speed, feed)
  const updateOpSetting = (id, key, value) => {
    setProgramOps(programOps.map(op => {
      if (op.id === id) {
        return { ...op, [key]: value };
      }
      return op;
    }));
  };

  // Toggle operation enabled/disabled
  const toggleOpEnabled = (id) => {
    setProgramOps(programOps.map(op => {
      if (op.id === id) {
        return { ...op, enabled: !op.enabled };
      }
      return op;
    }));
  };
  
  // Change operation side (for flip part)
  const changeOpSide = (id, newSide) => {
    setProgramOps(programOps.map(op => {
      if (op.id === id) {
        return { ...op, side: newSide };
      }
      return op;
    }));
    
    // Update tracking arrays
    const op = programOps.find(o => o.id === id);
    if (op) {
      if (newSide === 1) {
        setFlipPart(fp => ({
          ...fp,
          side1Ops: [...fp.side1Ops.filter(opId => opId !== id), id],
          side2Ops: fp.side2Ops.filter(opId => opId !== id),
        }));
      } else {
        setFlipPart(fp => ({
          ...fp,
          side1Ops: fp.side1Ops.filter(opId => opId !== id),
          side2Ops: [...fp.side2Ops.filter(opId => opId !== id), id],
        }));
      }
    }
  };

  // Generate complete G-code program in FANUC format
  const generateProgram = () => {
    const cfg = controllerConfig[controller];
    const lines = [];
    const enabledOps = programOps.filter(op => op.enabled);
    const material = materialLibrary[selectedMaterial];
    const holder = workpieceHolders[type][workpieceHolder.type];

    if (enabledOps.length === 0) {
      alert('Please add at least one operation');
      return;
    }

    // Separate operations by side for flip part
    const side1Ops = flipPart.enabled 
      ? enabledOps.filter(op => op.side === 1)
      : enabledOps;
    const side2Ops = flipPart.enabled 
      ? enabledOps.filter(op => op.side === 2)
      : [];

    // Program header - FANUC style
    lines.push('%');
    lines.push(cfg.programFormat(programSettings.programNum, programSettings.programName));
    lines.push(`(MATERIAL: ${material?.name || 'UNKNOWN'})`);
    lines.push(`(STOCK: DIA ${stockDimensions.diameter || 2.0}" x ${stockDimensions.length || 4.0}" LG)`);
    lines.push(`(WORKHOLDING: ${holder?.name || 'STANDARD'})`);
    if (type === 'turning' && workpieceHolder.type.includes('jaw')) {
      lines.push(`(JAW GRIP: ${workpieceHolder.gripLength}" DEPTH @ ${workpieceHolder.jawDiameter}" DIA)`);
    }
    if (flipPart.enabled) {
      lines.push(`(TWO-SIDED MACHINING - FLIP REQUIRED)`);
    }
    lines.push(`(DATE: ${new Date().toLocaleDateString()})`);
    lines.push(`(GENERATED BY G-CODE PRO)`);
    lines.push('');

    // Safe start block
    lines.push('G18 G40 G80;');
    lines.push('G20;');
    if (programSettings.useG50SpindleLimit && type === 'turning') {
      lines.push(`G50 S${programSettings.maxSpindle};`);
    }
    lines.push('');

    // Generate code for side 1 operations
    if (flipPart.enabled && side1Ops.length > 0) {
      lines.push('(========== SIDE 1 ==========);');
      lines.push('');
    }
    
    side1Ops.forEach((op, index) => {
      const opLines = generateOperationBlock(op, index, cfg, material);
      lines.push(...opLines);
    });

    // Insert flip part stop if enabled and there are side 2 ops
    if (flipPart.enabled && side2Ops.length > 0) {
      lines.push('');
      lines.push('(========================================);');
      lines.push(`(${flipPart.flipComment});`);
      lines.push('(========================================);');
      lines.push('T100;');
      lines.push('G28;');
      lines.push(`${flipPart.flipStopCode};`);
      lines.push('');
      lines.push('(========== SIDE 2 ==========);');
      lines.push('');
      
      // Re-initialize for side 2
      lines.push('G18 G40 G80;');
      lines.push('G20;');
      if (programSettings.useG50SpindleLimit && type === 'turning') {
        lines.push(`G50 S${programSettings.maxSpindle};`);
      }
      lines.push('');
      
      // Generate code for side 2 operations
      side2Ops.forEach((op, index) => {
        const opLines = generateOperationBlock(op, index + side1Ops.length, cfg, material);
        lines.push(...opLines);
      });
    }

    // Program end
    lines.push('');
    lines.push('T100;');
    lines.push('M30;');
    lines.push('%');

    setGcode(lines);
    if (onGenerateCode) onGenerateCode(lines);
  };
  
  // Generate a single operation block
  const generateOperationBlock = (op, index, cfg, material) => {
    const lines = [];
    const opDef = op.isLiveTool ? liveToolingOperations[op.type] : operations[op.type];
    const opNum = index + 1;
    
    lines.push('');
    lines.push(`N${opNum} (${op.name.toUpperCase()}${flipPart.enabled ? ` - SIDE ${op.side}` : ''});`);
    lines.push('G28;');
    lines.push(`T${op.tool}${String(op.tool).padStart(2, '0')} (${op.toolDesc || op.name.toUpperCase()}${op.toolNoseRadius ? ` x ${op.toolNoseRadius} TNR` : ''});`);
    
    if (programSettings.useG50SpindleLimit && type === 'turning') {
      lines.push(`G50 S${programSettings.maxSpindle};`);
    }
    
    lines.push(`G97 S${op.speed} ${op.spindleDir || 'M03'};`);
    lines.push(`G54 G00 X${(stockDimensions.diameter || 2.0) + 0.5} Z0.1${programSettings.useCoolant ? ' M08' : ''};`);
    
    // Use CSS (Constant Surface Speed) for turning
    if (type === 'turning' && material) {
      const avgSFM = Math.round((material.turning.roughing.sfm[0] + material.turning.roughing.sfm[1]) / 2);
      lines.push(`G96 S${avgSFM};`);
    }
    lines.push('');

    // Handle live tooling operations separately
    if (op.isLiveTool) {
      const liveLines = generateLiveToolingCode(op.type, op.params, {
        tool: op.tool,
        liveToolSpeed: op.speed,
        feed: op.feed,
        safeZ: programSettings.safeZ,
      }, controller);
      lines.push(...liveLines);
    } else {
      // Operation-specific code
      const opLines = generateOperationCode(op, cfg, type);
      lines.push(...opLines);
    }

    lines.push('');
    lines.push('G97 S500;');
    lines.push(`G00 Z${programSettings.safeZ}${programSettings.useCoolant ? ' M09' : ''};`);
    lines.push('G28;');
    lines.push('M01;');
    
    return lines;
  };

  // Generate code for a single operation with proper FANUC formatting
  const generateOperationCode = (op, cfg, machineType) => {
    const lines = [];
    const p = op.params;
    const feed = op.feed;
    const speed = op.speed || 1200;

    if (machineType === 'turning') {
      switch (op.type) {
        case 'facing':
          lines.push('(FACING CYCLE);');
          lines.push(`G00 Z0.;`);
          lines.push(`G01 X-0.062 F${feed.toFixed(4)};`);
          if (op.edgeBreak?.entry?.type === 'chamfer' && op.edgeBreak.entry.size > 0) {
            const cs = op.edgeBreak.entry.size;
            lines.push(`(CHAMFER BREAK EDGE);`);
            lines.push(`G00 X${(p.stockDia || stockDimensions.diameter).toFixed(4)} Z${cs.toFixed(4)};`);
            lines.push(`G01 X${((p.stockDia || stockDimensions.diameter) - cs * 2).toFixed(4)} Z0. F${(feed / 2).toFixed(4)};`);
          } else if (op.edgeBreak?.entry?.type === 'radius' && op.edgeBreak.entry.size > 0) {
            const rs = op.edgeBreak.entry.size;
            lines.push(`(RADIUS BREAK EDGE);`);
            lines.push(`G00 X${(p.stockDia || stockDimensions.diameter).toFixed(4)} Z${rs.toFixed(4)};`);
            lines.push(`G02 X${((p.stockDia || stockDimensions.diameter) - rs * 2).toFixed(4)} Z0. R${rs.toFixed(4)} F${(feed / 2).toFixed(4)};`);
          }
          lines.push(`G00 Z0.1;`);
          break;

        case 'od-roughing':
          lines.push('(OD ROUGHING CYCLE);');
          lines.push(`G71 U${p.doc.toFixed(3)} R0.05;`);
          lines.push(`G71 P10 Q20 U0.02 W0.005 F${feed.toFixed(4)};`);
          if (op.profileSteps && op.profileSteps.length > 0) {
            // Multi-step profile
            const steps = op.profileSteps;
            let zPos = 0;
            lines.push(`N10 G00 X${steps[0].diameter.toFixed(4)};`);
            // Entry edge break
            if (op.edgeBreak?.entry?.type === 'chamfer' && op.edgeBreak.entry.size > 0) {
              const cs = op.edgeBreak.entry.size;
              lines.push(`(CHAMFER BREAK EDGE - ENTRY);`);
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
              lines.push(`X${(steps[0].diameter + cs * 2).toFixed(4)} Z-${cs.toFixed(4)};`);
            } else if (op.edgeBreak?.entry?.type === 'radius' && op.edgeBreak.entry.size > 0) {
              const rs = op.edgeBreak.entry.size;
              lines.push(`(RADIUS BREAK EDGE - ENTRY);`);
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
              lines.push(`G02 X${(steps[0].diameter + rs * 2).toFixed(4)} Z-${rs.toFixed(4)} R${rs.toFixed(4)};`);
            } else {
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
            }
            for (let si = 0; si < steps.length; si++) {
              const step = steps[si];
              zPos += step.length;
              lines.push(`Z-${zPos.toFixed(4)};`);
              if (si < steps.length - 1) {
                const nextStep = steps[si + 1];
                // Edge break between steps
                if (op.edgeBreak?.exit?.type === 'chamfer' && op.edgeBreak.exit.size > 0) {
                  const cs = op.edgeBreak.exit.size;
                  lines.push(`X${(nextStep.diameter + cs * 2).toFixed(4)} Z-${(zPos + cs).toFixed(4)};`);
                  zPos += cs;
                } else if (op.edgeBreak?.exit?.type === 'radius' && op.edgeBreak.exit.size > 0) {
                  const rs = op.edgeBreak.exit.size;
                  lines.push(`G02 X${nextStep.diameter.toFixed(4)} Z-${(zPos + rs).toFixed(4)} R${rs.toFixed(4)};`);
                  zPos += rs;
                } else {
                  lines.push(`X${nextStep.diameter.toFixed(4)};`);
                }
              }
            }
            lines.push(`X${((p.stockDia || stockDimensions.diameter) + 0.06).toFixed(4)};`);
            lines.push('N20 G40 X' + ((p.stockDia || stockDimensions.diameter) + 0.5).toFixed(4) + ';');
          } else {
            // Single straight profile (original behavior + edge breaks)
            lines.push(`N10 G00 X${p.finishDia.toFixed(4)};`);
            if (op.edgeBreak?.entry?.type === 'chamfer' && op.edgeBreak.entry.size > 0) {
              const cs = op.edgeBreak.entry.size;
              lines.push(`(CHAMFER BREAK EDGE - ENTRY);`);
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
              lines.push(`X${(p.finishDia + cs * 2).toFixed(4)} Z-${cs.toFixed(4)};`);
              lines.push(`Z-${p.length.toFixed(4)};`);
            } else if (op.edgeBreak?.entry?.type === 'radius' && op.edgeBreak.entry.size > 0) {
              const rs = op.edgeBreak.entry.size;
              lines.push(`(RADIUS BREAK EDGE - ENTRY);`);
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
              lines.push(`G02 X${(p.finishDia + rs * 2).toFixed(4)} Z-${rs.toFixed(4)} R${rs.toFixed(4)};`);
              lines.push(`G01 Z-${p.length.toFixed(4)};`);
            } else {
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
              lines.push(`Z-${p.length.toFixed(4)};`);
            }
            // Exit edge break
            if (op.edgeBreak?.exit?.type === 'chamfer' && op.edgeBreak.exit.size > 0) {
              const cs = op.edgeBreak.exit.size;
              lines.push(`(CHAMFER BREAK EDGE - EXIT);`);
              lines.push(`X${(p.finishDia + cs * 2).toFixed(4)} Z-${(p.length + cs).toFixed(4)};`);
            } else if (op.edgeBreak?.exit?.type === 'radius' && op.edgeBreak.exit.size > 0) {
              const rs = op.edgeBreak.exit.size;
              lines.push(`(RADIUS BREAK EDGE - EXIT);`);
              lines.push(`G02 X${(p.finishDia + rs * 2).toFixed(4)} Z-${(p.length + rs).toFixed(4)} R${rs.toFixed(4)};`);
            }
            lines.push(`X${((p.stockDia || stockDimensions.diameter) + 0.06).toFixed(4)};`);
            lines.push('N20 G40 X' + ((p.stockDia || stockDimensions.diameter) + 0.5).toFixed(4) + ';');
          }
          break;

        case 'od-finishing':
          lines.push('(FINISH O.D.);');
          lines.push(`G00 X${(p.finishDia + 0.1).toFixed(4)} Z0.1;`);
          lines.push(`G96 S390;`);
          lines.push(`G00 Z0.;`);
          lines.push(`G01 X-0.032 F${(feed * 0.75).toFixed(4)};`);
          lines.push(`G00 X${(p.finishDia + 0.5).toFixed(4)} Z0.1;`);
          lines.push(`G70 P10 Q20;`);
          break;

        case 'od-threading':
          {
            const majorD = parseFloat(p.majorDia) || 0.500;
            const minorD = parseFloat(p.minorDia) || (majorD - 0.092);
            const threadP = parseFloat(p.pitch) || 0.07692;
            const tpiVal = parseFloat(p.tpi) || Math.round(1 / threadP);
            const threadLen = parseFloat(p.length) || 1.0;
            const totalD = (majorD - minorD) / 2;
            const leadIn = threadP;
            const startZp = leadIn;
            const endZp = -(threadLen + leadIn);
            const numP = Math.max(4, Math.ceil(totalD / 0.005));
            
            lines.push(`(THREADING - ${p.threadDesignation || tpiVal + ' TPI'});`);
            lines.push(`(MAJOR: ${majorD.toFixed(4)} MINOR: ${minorD.toFixed(4)} PITCH: ${threadP.toFixed(5)});`);
            lines.push(`(${numP} PASSES + ${p.springPasses || 2} SPRING PASSES - LONG HAND);`);
            lines.push(`G97 S${Math.min(1200, Math.round(speed * 0.6))};`);
            
            for (let pi = 1; pi <= numP; pi++) {
              const cumDepth = totalD * Math.sqrt(pi / numP);
              const cutDia = majorD - (cumDepth * 2);
              lines.push(`(PASS ${pi} - X${cutDia.toFixed(4)});`);
              lines.push(`G00 X${majorD.toFixed(4)} Z${startZp.toFixed(4)};`);
              lines.push(`G00 X${cutDia.toFixed(4)};`);
              lines.push(`G32 Z${endZp.toFixed(4)} F${threadP.toFixed(5)};`);
              lines.push(`G00 X${(majorD + 0.050).toFixed(4)};`);
              lines.push(`G00 Z${startZp.toFixed(4)};`);
            }
            for (let si = 0; si < (p.springPasses || 2); si++) {
              const finalX = majorD - (totalD * 2);
              lines.push(`(SPRING PASS ${si + 1});`);
              lines.push(`G00 X${majorD.toFixed(4)} Z${startZp.toFixed(4)};`);
              lines.push(`G00 X${finalX.toFixed(4)};`);
              lines.push(`G32 Z${endZp.toFixed(4)} F${threadP.toFixed(5)};`);
              lines.push(`G00 X${(majorD + 0.050).toFixed(4)};`);
              lines.push(`G00 Z${startZp.toFixed(4)};`);
            }
            lines.push(`G96 S${speed};`);
          }
          break;

        case 'id-boring':
          lines.push('(BORING CYCLE);');
          lines.push(`G00 X${(p.startDia - 0.1).toFixed(4)} Z0.1;`);
          lines.push(`G71 U0.04 R0.03;`);
          lines.push(`G71 P30 Q40 U-0.02 W0.005 F${feed.toFixed(4)};`);
          if (op.profileSteps && op.profileSteps.length > 0) {
            const steps = op.profileSteps;
            let zPos = 0;
            lines.push(`N30 G00 X${steps[0].diameter.toFixed(4)};`);
            if (op.edgeBreak?.entry?.type === 'chamfer' && op.edgeBreak.entry.size > 0) {
              const cs = op.edgeBreak.entry.size;
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
              lines.push(`X${(steps[0].diameter - cs * 2).toFixed(4)} Z-${cs.toFixed(4)};`);
            } else if (op.edgeBreak?.entry?.type === 'radius' && op.edgeBreak.entry.size > 0) {
              const rs = op.edgeBreak.entry.size;
              lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
              lines.push(`G03 X${(steps[0].diameter - rs * 2).toFixed(4)} Z-${rs.toFixed(4)} R${rs.toFixed(4)};`);
            } else {
              lines.push(`G01 Z0.;`);
            }
            for (let si = 0; si < steps.length; si++) {
              const step = steps[si];
              zPos += step.length;
              lines.push(`Z-${zPos.toFixed(4)};`);
              if (si < steps.length - 1) {
                lines.push(`X${steps[si + 1].diameter.toFixed(4)};`);
              }
            }
            lines.push(`X${(p.startDia - 0.1).toFixed(4)};`);
            lines.push('N40;');
          } else {
            lines.push(`N30 G00 X${p.finishDia.toFixed(4)};`);
            if (op.edgeBreak?.entry?.type === 'chamfer' && op.edgeBreak.entry.size > 0) {
              const cs = op.edgeBreak.entry.size;
              lines.push(`G01 Z0.;`);
              lines.push(`X${(p.finishDia - cs * 2).toFixed(4)} Z-${cs.toFixed(4)};`);
              lines.push(`Z-${p.depth.toFixed(4)};`);
            } else if (op.edgeBreak?.entry?.type === 'radius' && op.edgeBreak.entry.size > 0) {
              const rs = op.edgeBreak.entry.size;
              lines.push(`G01 Z0.;`);
              lines.push(`G03 X${(p.finishDia - rs * 2).toFixed(4)} Z-${rs.toFixed(4)} R${rs.toFixed(4)};`);
              lines.push(`G01 Z-${p.depth.toFixed(4)};`);
            } else {
              lines.push(`G01 Z0.;`);
              lines.push(`Z-${p.depth.toFixed(4)};`);
            }
            lines.push(`X${(p.startDia - 0.1).toFixed(4)};`);
            lines.push('N40;');
          }
          break;

        case 'grooving':
          lines.push('(GROOVING);');
          lines.push(`G00 X${(p.grooveDia + 0.2).toFixed(4)} Z${p.zPosition.toFixed(4)};`);
          lines.push(`G75 R0.03;`);
          lines.push(`G75 X${p.grooveDia.toFixed(4)} Z${(p.zPosition - p.grooveWidth).toFixed(4)} P2000 Q${(p.grooveWidth * 1000).toFixed(0)} F${(feed / 2).toFixed(4)};`);
          break;

        case 'parting':
          lines.push('(PARTING);');
          lines.push(`G00 X${(stockDimensions.diameter + 0.2).toFixed(4)} Z${p.zPosition.toFixed(4)};`);
          lines.push(`G01 X${(p.partDia || 0).toFixed(4)} F${(feed / 3).toFixed(4)};`);
          lines.push(`G00 X${(stockDimensions.diameter + 0.5).toFixed(4)};`);
          break;

        case 'drilling':
          lines.push(`(DRILL ${p.drillDia.toFixed(3)} DIA. x ${p.depth.toFixed(3)} DEPTH);`);
          lines.push(`G00 X0. Z1.;`);
          lines.push(`G83 Z-${p.depth.toFixed(4)} Q${p.peck.toFixed(3)} R0.1 F${(feed * 0.8).toFixed(4)};`);
          lines.push('G80 G00 Z0.1;');
          break;

        case 'taper':
          lines.push('(TAPER TURNING);');
          lines.push(`G00 X${(p.startDia + 0.1).toFixed(4)} Z0.1;`);
          lines.push(`G01 X${p.startDia.toFixed(4)} Z0. F${feed.toFixed(4)};`);
          lines.push(`X${p.endDia.toFixed(4)} Z-${p.length.toFixed(4)};`);
          lines.push(`G00 X${(p.startDia + 0.5).toFixed(4)};`);
          break;

        case 'pattern-repeat':
          {
            const stockAllow = p.stockAllowance || 0.120;
            const docPR = p.doc || 0.040;
            const numDiv = Math.ceil(stockAllow / docPR);
            const xFA = p.finishAllowance || 0.015;
            const zFA = (p.finishAllowance || 0.015) / 2;
            
            lines.push('(G73 PATTERN REPEATING - CASTING/FORGING);');
            lines.push(`(STOCK ALLOWANCE: ${stockAllow}" / ${numDiv} PASSES);`);
            lines.push(`G73 U${stockAllow} W${(stockAllow / 2).toFixed(4)} R${numDiv};`);
            lines.push(`G73 P50 Q60 U${xFA} W${zFA} F${feed.toFixed(4)};`);
            lines.push(`N50 G00 X${p.finishDia.toFixed(4)};`);
            lines.push(`G01 Z0. F${(feed / 2).toFixed(4)};`);
            if (p.profileType === 'taper') {
              lines.push(`X${(p.endDia || p.finishDia).toFixed(4)} Z-${p.length.toFixed(4)};`);
            } else {
              lines.push(`Z-${p.length.toFixed(4)};`);
            }
            lines.push(`N60 X${(p.finishDia + 0.5).toFixed(4)};`);
            lines.push('');
            lines.push('(G73 FINISH - G70);');
            lines.push('G70 P50 Q60;');
          }
          break;

        default:
          lines.push('(CUSTOM TURNING OPERATION);');
      }
    } else {
      // Milling operations
      switch (op.type) {
        case 'face-mill': {
          lines.push('(FACE MILLING);');
          const stepover = p.toolDia * (p.stepover / 100);
          const passes = Math.ceil(p.length / stepover);
          lines.push(`G00 X-${(p.toolDia / 2).toFixed(4)} Y-${(stepover / 2).toFixed(4)};`);
          lines.push(`G00 Z0.1;`);
          lines.push(`G01 Z-${p.depth.toFixed(4)} F${(feed / 3).toFixed(1)};`);
          for (let i = 0; i < passes; i++) {
            const y = (i * stepover).toFixed(4);
            if (i % 2 === 0) {
              lines.push(`G01 X${(p.width + p.toolDia / 2).toFixed(4)} Y${y} F${feed.toFixed(1)};`);
            } else {
              lines.push(`G01 X-${(p.toolDia / 2).toFixed(4)} Y${y} F${feed.toFixed(1)};`);
            }
          }
          break;
        }

        case 'pocket-rect': {
          lines.push('(RECTANGULAR POCKET);');
          const so = p.toolDia * 0.5;
          const numZ = Math.ceil(p.depth / p.stepdown);
          lines.push(`G00 X${so.toFixed(4)} Y${so.toFixed(4)};`);
          lines.push(`G00 Z0.2;`);
          for (let z = 1; z <= numZ; z++) {
            const zDepth = Math.min(z * p.stepdown, p.depth).toFixed(4);
            lines.push(`G01 Z-${zDepth} F${(feed / 3).toFixed(1)};`);
            lines.push(`G01 X${(p.width - so).toFixed(4)} F${feed.toFixed(1)};`);
            lines.push(`Y${(p.length - so).toFixed(4)};`);
            lines.push(`X${so.toFixed(4)};`);
            lines.push(`Y${so.toFixed(4)};`);
          }
          break;
        }

        case 'pocket-circ': {
          lines.push('(CIRCULAR POCKET);');
          const radius = (p.diameter - p.toolDia) / 2;
          lines.push(`G00 X0 Y0;`);
          lines.push(`G00 Z0.2;`);
          const zPasses = Math.ceil(p.depth / p.stepdown);
          for (let z = 1; z <= zPasses; z++) {
            const zD = Math.min(z * p.stepdown, p.depth).toFixed(4);
            lines.push(`G01 Z-${zD} F${(feed / 3).toFixed(1)};`);
            lines.push(`G02 X${radius.toFixed(4)} Y0 I${(radius / 2).toFixed(4)} J0 F${feed.toFixed(1)};`);
            lines.push(`G02 X${radius.toFixed(4)} Y0 I-${radius.toFixed(4)} J0;`);
          }
          break;
        }

        case 'drill':
          lines.push('(DRILLING);');
          lines.push(`G00 X${p.x} Y${p.y};`);
          lines.push(`G00 Z${p.retract};`);
          lines.push(`G81 Z-${p.depth.toFixed(4)} R${p.retract} F${feed.toFixed(1)};`);
          lines.push('G80;');
          break;

        case 'peck-drill':
          lines.push('(PECK DRILLING);');
          lines.push(`G00 X${p.x} Y${p.y};`);
          lines.push(`G00 Z${p.retract};`);
          lines.push(`G83 Z-${p.depth.toFixed(4)} Q${p.peck.toFixed(3)} R${p.retract} F${feed.toFixed(1)};`);
          lines.push('G80;');
          break;

        case 'tap': {
          lines.push('(TAPPING);');
          const tapFeed = (op.speed / p.pitch).toFixed(4);
          lines.push(`G00 X${p.x} Y${p.y};`);
          lines.push(`G00 Z0.1;`);
          lines.push(`G84 Z-${p.depth.toFixed(4)} R0.1 F${tapFeed};`);
          lines.push('G80;');
          break;
        }

        case 'contour':
          lines.push('(CONTOUR MILLING);');
          lines.push('(DEFINE CONTOUR PATH BELOW);');
          break;

        case 'slot':
          lines.push('(SLOT MILLING);');
          lines.push(`G00 X0 Y0;`);
          lines.push(`G00 Z0.1;`);
          lines.push(`G01 Z-${p.depth.toFixed(4)} F${(feed / 3).toFixed(1)};`);
          lines.push(`G01 X${p.length.toFixed(4)} F${feed.toFixed(1)};`);
          break;

        case 'helical-bore': {
          lines.push('(HELICAL BORE);');
          const boreRadius = (p.diameter - p.toolDia) / 2;
          lines.push(`G00 X${boreRadius.toFixed(4)} Y0;`);
          lines.push(`G00 Z0.1;`);
          const helixPasses = Math.ceil(p.depth / p.helixPitch);
          for (let i = 0; i < helixPasses; i++) {
            const zLevel = Math.min((i + 1) * p.helixPitch, p.depth).toFixed(4);
            lines.push(`G02 X${boreRadius.toFixed(4)} Y0 Z-${zLevel} I-${boreRadius.toFixed(4)} J0 F${feed.toFixed(1)};`);
          }
          break;
        }

        default:
          lines.push('(CUSTOM MILLING OPERATION);');
      }
    }

    return lines;
  };

  // Copy code to clipboard
  const copyCode = async () => {
    const text = gcode.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  // Download code as file
  const downloadCode = () => {
    const text = gcode.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `O${String(programSettings.programNum).padStart(4, '0')}_${programSettings.programName || 'program'}.nc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Save program to library
  const saveProgram = async () => {
    if (gcode.length === 0) return;
    
    setSaving(true);
    try {
      const programData = {
        name: `O${String(programSettings.programNum).padStart(4, '0')}_${programSettings.programName || 'Program'}`,
        type: type,
        controller: controller,
        gcode: gcode.join('\n'),
        description: `${type === 'turning' ? 'Turning' : 'Milling'} program with ${programOps.length} operations`,
        material: selectedMaterial,
        operations: programOps.map(op => ({
          type: op.type,
          tool: op.tool,
          params: op.params
        }))
      };
      
      await axios.post(`${API}/programs`, programData);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving program:', error);
      alert('Failed to save program. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const accentColor = type === 'turning' ? 'industrial-success' : 'violet-400';
  const accentBg = type === 'turning' ? 'industrial-success' : 'violet-500';
  const holders = workpieceHolders[type];

  return (
    <div className="space-y-4 animate-in pb-8">
      {/* Material & Stock Setup */}
      <MaterialSetup
        machineType={type}
        initialMaterial={selectedMaterial}
        initialStock={stockDimensions}
        onMaterialChange={(matKey, mat) => setSelectedMaterial(matKey)}
        onStockChange={(stock) => setStockDimensions(stock)}
        onCuttingParamsChange={(params) => setCuttingParams(params)}
      />
      
      {/* Workpiece Holder Setup */}
      <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
        <button
          onClick={() => setShowHolderSetup(!showHolderSetup)}
          className="w-full flex items-center justify-between p-4 hover:bg-industrial-surface-highlight transition-colors"
          data-testid="holder-setup-toggle"
        >
          <div className="flex items-center gap-2">
            <Anchor className="w-4 h-4 text-industrial-readout" />
            <h3 className="font-heading font-semibold text-industrial-text">Workpiece Holder</h3>
            <span className="text-xs text-industrial-text-secondary ml-2">
              ({holders[workpieceHolder.type]?.name || 'None'})
            </span>
          </div>
          <ChevronDown className={`w-5 h-5 text-industrial-text-secondary transition-transform ${showHolderSetup ? 'rotate-180' : ''}`} />
        </button>
        
        {showHolderSetup && (
          <div className="p-4 pt-0 space-y-4 border-t border-industrial-border animate-in">
            <div>
              <label className="text-xs text-industrial-text-secondary mb-2 block">Holder Type</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(holders).map(([key, holder]) => (
                  <button
                    key={key}
                    onClick={() => setWorkpieceHolder(h => ({ ...h, type: key }))}
                    data-testid={`holder-${key}`}
                    className={`p-3 rounded-lg border text-left transition-all text-xs ${
                      workpieceHolder.type === key
                        ? `bg-${accentBg}/20 border-${accentColor} text-${accentColor}`
                        : 'bg-industrial-surface-highlight border-industrial-border text-industrial-text hover:border-industrial-safety'
                    }`}
                  >
                    <div className="font-medium">{holder.name}</div>
                    <div className="text-industrial-text-secondary text-[10px] mt-0.5">{holder.description}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Holder-specific parameters */}
            {type === 'turning' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Jaw Grip Diameter (in)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={workpieceHolder.jawDiameter}
                    onChange={(e) => setWorkpieceHolder(h => ({ ...h, jawDiameter: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    data-testid="jaw-diameter"
                  />
                </div>
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Grip Depth (in)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={workpieceHolder.gripLength}
                    onChange={(e) => setWorkpieceHolder(h => ({ ...h, gripLength: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    data-testid="grip-length"
                  />
                </div>
              </div>
            )}
            
            {type === 'milling' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Vise Opening (in)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={workpieceHolder.viseWidth}
                    onChange={(e) => setWorkpieceHolder(h => ({ ...h, viseWidth: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    data-testid="vise-width"
                  />
                </div>
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Parallel Height (in)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={workpieceHolder.parallelHeight}
                    onChange={(e) => setWorkpieceHolder(h => ({ ...h, parallelHeight: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    data-testid="parallel-height"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Flip Part / Two-Sided Machining Setup */}
      <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
        <button
          onClick={() => setShowFlipSetup(!showFlipSetup)}
          className="w-full flex items-center justify-between p-4 hover:bg-industrial-surface-highlight transition-colors"
          data-testid="flip-setup-toggle"
        >
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-industrial-warning" />
            <h3 className="font-heading font-semibold text-industrial-text">Two-Sided Machining</h3>
            <span className={`text-xs px-2 py-0.5 rounded ml-2 ${flipPart.enabled ? 'bg-industrial-success/20 text-industrial-success' : 'bg-industrial-border text-industrial-text-secondary'}`}>
              {flipPart.enabled ? 'ENABLED' : 'OFF'}
            </span>
          </div>
          <ChevronDown className={`w-5 h-5 text-industrial-text-secondary transition-transform ${showFlipSetup ? 'rotate-180' : ''}`} />
        </button>
        
        {showFlipSetup && (
          <div className="p-4 pt-0 space-y-4 border-t border-industrial-border animate-in">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-industrial-text">Enable Flip Part Process</div>
                <div className="text-xs text-industrial-text-secondary">Machine both sides of workpiece with flip</div>
              </div>
              <button
                onClick={() => setFlipPart(fp => ({ ...fp, enabled: !fp.enabled }))}
                data-testid="flip-toggle"
                className={`w-14 h-7 rounded-full transition-colors relative ${
                  flipPart.enabled ? 'bg-industrial-success' : 'bg-industrial-border'
                }`}
              >
                <span className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                  flipPart.enabled ? 'left-8' : 'left-1'
                }`} />
              </button>
            </div>
            
            {flipPart.enabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Stop Code at Flip</label>
                    <select
                      value={flipPart.flipStopCode}
                      onChange={(e) => setFlipPart(fp => ({ ...fp, flipStopCode: e.target.value }))}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                      data-testid="flip-stop-code"
                    >
                      <option value="M01">M01 - Optional Stop</option>
                      <option value="M00">M00 - Mandatory Stop</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Current Side</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setFlipPart(fp => ({ ...fp, currentSide: 1 }))}
                        data-testid="side-1-btn"
                        className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                          flipPart.currentSide === 1 
                            ? 'bg-industrial-success text-black' 
                            : 'bg-industrial-border text-industrial-text-secondary'
                        }`}
                      >
                        Side 1
                      </button>
                      <button
                        onClick={() => setFlipPart(fp => ({ ...fp, currentSide: 2 }))}
                        data-testid="side-2-btn"
                        className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                          flipPart.currentSide === 2 
                            ? 'bg-industrial-warning text-black' 
                            : 'bg-industrial-border text-industrial-text-secondary'
                        }`}
                      >
                        Side 2
                      </button>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Flip Comment</label>
                  <input
                    type="text"
                    value={flipPart.flipComment}
                    onChange={(e) => setFlipPart(fp => ({ ...fp, flipComment: e.target.value }))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                    placeholder="FLIP PART - RECHUCK AND VERIFY"
                    data-testid="flip-comment"
                  />
                </div>
                
                <div className="flex gap-4 text-xs text-industrial-text-secondary">
                  <span>Side 1: <span className="text-industrial-success font-mono">{programOps.filter(op => op.side === 1 && op.enabled).length} ops</span></span>
                  <span>Side 2: <span className="text-industrial-warning font-mono">{programOps.filter(op => op.side === 2 && op.enabled).length} ops</span></span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Program Settings */}
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Settings2 className="w-4 h-4 text-industrial-readout" />
          <h3 className="font-heading font-semibold text-industrial-text">Program Settings</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-industrial-text-secondary mb-1 block">Program #</label>
            <input
              type="number"
              value={programSettings.programNum}
              onChange={(e) => setProgramSettings(s => ({ ...s, programNum: parseInt(e.target.value) || 1 }))}
              className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
              data-testid="program-num"
            />
          </div>
          <div>
            <label className="text-xs text-industrial-text-secondary mb-1 block">Program Name</label>
            <input
              type="text"
              value={programSettings.programName}
              onChange={(e) => setProgramSettings(s => ({ ...s, programName: e.target.value }))}
              placeholder="Part_001"
              className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
              data-testid="program-name"
            />
          </div>
          <div>
            <label className="text-xs text-industrial-text-secondary mb-1 block">Max Spindle RPM</label>
            <input
              type="number"
              value={programSettings.maxSpindle}
              onChange={(e) => setProgramSettings(s => ({ ...s, maxSpindle: parseInt(e.target.value) || 3500 }))}
              className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={programSettings.useCoolant}
                onChange={(e) => setProgramSettings(s => ({ ...s, useCoolant: e.target.checked }))}
                className="w-4 h-4 accent-industrial-safety"
              />
              <span className="text-xs text-industrial-text-secondary">Coolant</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={programSettings.useG50SpindleLimit}
                onChange={(e) => setProgramSettings(s => ({ ...s, useG50SpindleLimit: e.target.checked }))}
                className="w-4 h-4 accent-industrial-safety"
              />
              <span className="text-xs text-industrial-text-secondary">G50 Limit</span>
            </label>
          </div>
        </div>
      </div>

      {/* Add Turning/Milling Operations */}
      <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-industrial-text">
            {type === 'turning' ? 'Turning Operations' : 'Milling Operations'}
          </h3>
          {flipPart.enabled && (
            <span className={`text-xs px-2 py-1 rounded ${
              flipPart.currentSide === 1 
                ? 'bg-industrial-success/20 text-industrial-success' 
                : 'bg-industrial-warning/20 text-industrial-warning'
            }`}>
              Adding to Side {flipPart.currentSide}
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(operations).map(([key, op]) => (
            <button
              key={key}
              onClick={() => addOperation(key, false)}
              data-testid={`add-op-${key}`}
              className={`p-2 rounded-lg border text-center transition-all text-xs bg-industrial-surface-highlight border-industrial-border text-industrial-text hover:border-${accentColor} flex items-center justify-center gap-1`}
            >
              <Plus className="w-3 h-3" />
              {op.name}
            </button>
          ))}
        </div>
      </div>

      {/* Live Tooling Operations (for turning only) */}
      {type === 'turning' && (
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <button
            onClick={() => setShowLiveTooling(!showLiveTooling)}
            className="w-full flex items-center justify-between"
            data-testid="live-tooling-toggle"
          >
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-semibold text-industrial-readout">Live Tooling</h3>
              <span className="text-xs px-2 py-0.5 rounded bg-industrial-readout/20 text-industrial-readout font-mono">
                {turnMillConfig[activeMachineConfig]?.axes?.join('/') || 'X/Z'}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showLiveTooling ? 'rotate-180' : ''}`} />
          </button>
          
          {showLiveTooling && (
            <div className="mt-3 pt-3 border-t border-industrial-border space-y-4">
              {/* Machine Config Selector */}
              <div>
                <label className="text-xs text-industrial-text-secondary mb-2 block">Machine Configuration</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(turnMillConfig).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => setActiveMachineConfig(key)}
                      data-testid={`machine-cfg-${key}`}
                      className={`p-2 rounded-lg border text-left transition-all text-xs ${
                        activeMachineConfig === key
                          ? 'bg-industrial-readout/20 border-industrial-readout text-industrial-readout'
                          : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-industrial-safety'
                      }`}
                    >
                      <div className="font-medium">{cfg.name}</div>
                      <div className="text-[10px] mt-0.5 text-industrial-text-secondary">{cfg.axes?.join(', ')} axis</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Available Operations by Category */}
              {Object.entries(liveToolingCategories).map(([catKey, category]) => {
                const availableOps = getAvailableOperations(activeMachineConfig);
                const catOps = category.ops.filter(opKey => availableOps[opKey]);
                
                if (catOps.length === 0) return null;
                
                return (
                  <div key={catKey}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${
                        catKey === 'axial' ? 'bg-cyan-400' :
                        catKey === 'radial' ? 'bg-amber-400' :
                        catKey === 'angular' ? 'bg-rose-400' :
                        'bg-violet-400'
                      }`} />
                      <span className="text-xs font-medium text-industrial-text">{category.name}</span>
                      <span className="text-[10px] text-industrial-text-secondary">{category.description}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {catOps.map(opKey => {
                        const op = liveToolingOperations[opKey];
                        return (
                          <button
                            key={opKey}
                            onClick={() => addOperation(opKey, true)}
                            data-testid={`add-live-${opKey}`}
                            className={`p-2 rounded-lg border text-left transition-all text-xs bg-industrial-bg border-industrial-border text-industrial-text hover:border-industrial-readout ${
                              op.requiresBAxis ? 'border-l-2 border-l-rose-400/60' :
                              op.requiresYAxis ? 'border-l-2 border-l-amber-400/60' : ''
                            }`}
                          >
                            <div className="flex items-center gap-1">
                              <Plus className="w-3 h-3 text-industrial-readout flex-shrink-0" />
                              <span className="truncate">{op.name}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Machine Preset Info */}
              {turnMillConfig[activeMachineConfig]?.preset && machinePresets[turnMillConfig[activeMachineConfig].preset] && (
                <div className="p-2 rounded bg-industrial-bg/50 border border-industrial-border/50">
                  <div className="text-[10px] text-industrial-text-secondary">
                    Preset: <span className="text-industrial-readout font-mono">{machinePresets[turnMillConfig[activeMachineConfig].preset].description}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Operations List */}
      <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-industrial-border bg-industrial-surface-highlight">
          <h3 className="font-heading font-semibold text-industrial-text">
            Operation Sequence ({programOps.filter(op => op.enabled).length} operations)
          </h3>
        </div>
        
        {/* Sequence Warnings */}
        {sequenceWarnings.length > 0 && (
          <div className="p-3 space-y-2 bg-industrial-danger/10 border-b border-industrial-danger/30">
            {sequenceWarnings.map((warning, idx) => (
              <div 
                key={idx} 
                className={`flex items-start gap-2 p-2 rounded ${
                  warning.type === 'error' 
                    ? 'bg-industrial-danger/20 border border-industrial-danger/40' 
                    : 'bg-industrial-warning/20 border border-industrial-warning/40'
                }`}
              >
                <span className={`text-lg ${warning.type === 'error' ? 'text-industrial-danger' : 'text-industrial-warning'}`}>
                  {warning.type === 'error' ? '⚠' : '⚡'}
                </span>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    warning.type === 'error' ? 'text-industrial-danger' : 'text-industrial-warning'
                  }`}>
                    {warning.message}
                  </p>
                  {warning.suggestedOps && warning.suggestedOps.length > 0 && (
                    <button
                      onClick={() => addSuggestedOperations(
                        warning.suggestedOps, 
                        'id-boring',
                        { 
                          recommendedDrillSize: warning.recommendedDrillSize,
                          boreDiameter: warning.boreDiameter 
                        }
                      )}
                      className="mt-2 px-3 py-1 text-xs bg-industrial-safety text-black rounded font-medium hover:bg-industrial-safety/80 transition-colors"
                      data-testid="add-suggested-ops"
                    >
                      + Auto-Add: {warning.suggestedOps.map(op => operations[op]?.name || op).join(' + ')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {programOps.length === 0 ? (
          <div className="p-8 text-center text-industrial-text-secondary">
            <p>No operations added yet.</p>
            <p className="text-sm mt-1">Click buttons above to add operations.</p>
          </div>
        ) : (
          <div className="divide-y divide-industrial-border">
            {programOps.map((op, index) => (
              <div 
                key={op.id} 
                className={`transition-all ${!op.enabled ? 'opacity-50' : ''}`}
              >
                {/* Operation Header */}
                <div 
                  className="flex items-center gap-2 p-3 cursor-pointer hover:bg-industrial-surface-highlight"
                  onClick={() => setExpandedOp(expandedOp === op.id ? null : op.id)}
                >
                  <GripVertical className="w-4 h-4 text-industrial-text-secondary" />
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); moveOperation(index, -1); }}
                      disabled={index === 0}
                      className="p-1 rounded hover:bg-industrial-border disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveOperation(index, 1); }}
                      disabled={index === programOps.length - 1}
                      className="p-1 rounded hover:bg-industrial-border disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  <span className={`w-6 h-6 rounded-full bg-${accentBg}/20 text-${accentColor} text-xs flex items-center justify-center font-bold`}>
                    {index + 1}
                  </span>
                  
                  <div className="flex-1">
                    <span className="font-medium text-industrial-text">{op.name}</span>
                    <span className="text-xs text-industrial-text-secondary ml-2">T{op.tool}</span>
                    {flipPart.enabled && (
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                        op.side === 1 
                          ? 'bg-industrial-success/20 text-industrial-success' 
                          : 'bg-industrial-warning/20 text-industrial-warning'
                      }`}>
                        S{op.side}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); toggleOpEnabled(op.id); }}
                    className={`px-2 py-1 rounded text-xs ${op.enabled ? 'bg-industrial-success/20 text-industrial-success' : 'bg-industrial-border text-industrial-text-secondary'}`}
                  >
                    {op.enabled ? 'ON' : 'OFF'}
                  </button>

                  <button
                    onClick={(e) => { e.stopPropagation(); removeOperation(op.id); }}
                    className="p-1 rounded hover:bg-industrial-danger/20 text-industrial-danger"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <ChevronRight className={`w-4 h-4 text-industrial-text-secondary transition-transform ${expandedOp === op.id ? 'rotate-90' : ''}`} />
                </div>

                {/* Expanded Parameters */}
                {expandedOp === op.id && (
                  <div className="px-4 pb-4 pt-2 bg-industrial-bg/50 border-t border-industrial-border animate-in">
                    {/* Side Selection (for flip part) */}
                    {flipPart.enabled && (
                      <div className="mb-3">
                        <label className="text-xs text-industrial-text-secondary mb-1 block">Assign to Side</label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => changeOpSide(op.id, 1)}
                            className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${
                              op.side === 1 
                                ? 'bg-industrial-success text-black' 
                                : 'bg-industrial-border text-industrial-text-secondary'
                            }`}
                          >
                            Side 1
                          </button>
                          <button
                            onClick={() => changeOpSide(op.id, 2)}
                            className={`flex-1 py-2 rounded text-xs font-medium transition-colors ${
                              op.side === 2 
                                ? 'bg-industrial-warning text-black' 
                                : 'bg-industrial-border text-industrial-text-secondary'
                            }`}
                          >
                            Side 2
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Tool & Cutting Settings */}
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div>
                        <label className="text-xs text-industrial-text-secondary mb-1 block">Tool #</label>
                        <input
                          type="number"
                          value={op.tool}
                          onChange={(e) => updateOpSetting(op.id, 'tool', parseInt(e.target.value))}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-industrial-text-secondary mb-1 block">Insert Preset</label>
                        <select
                          value=""
                          onChange={(e) => {
                            const preset = INSERT_PRESETS.find(p => p.code === e.target.value);
                            if (preset) {
                              setProgramOps(programOps.map(o => o.id === op.id ? {
                                ...o,
                                toolNoseRadius: preset.noseRadiusIn,
                                toolDesc: `${preset.code} ${preset.label}`,
                              } : o));
                            }
                          }}
                          data-testid={`insert-preset-${op.id}`}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-xs text-industrial-text"
                        >
                          <option value="">{op.toolNoseRadius ? `${op.toolDesc || 'Custom'} (${op.toolNoseRadius}" TNR)` : 'Select insert...'}</option>
                          {INSERT_PRESETS.map(p => (
                            <option key={p.code} value={p.code}>{p.code} — {p.tipAngle}° / {p.noseRadiusIn}" TNR</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="text-xs text-industrial-text-secondary mb-1 block">Speed (RPM)</label>
                        <input
                          type="number"
                          value={op.speed}
                          onChange={(e) => updateOpSetting(op.id, 'speed', parseInt(e.target.value))}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-industrial-text-secondary mb-1 block">
                          Feed ({type === 'turning' ? 'IPR' : 'IPM'})
                        </label>
                        <input
                          type="number"
                          step="0.001"
                          value={op.feed}
                          onChange={(e) => updateOpSetting(op.id, 'feed', parseFloat(e.target.value))}
                          className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-industrial-text-secondary mb-1 block">Spindle Dir</label>
                        <div className="flex rounded overflow-hidden border border-industrial-border">
                          <button
                            onClick={() => updateOpSetting(op.id, 'spindleDir', 'M03')}
                            data-testid={`spindle-cw-${op.id}`}
                            className={`flex-1 py-1.5 text-xs font-mono font-medium transition-colors ${
                              (op.spindleDir || 'M03') === 'M03'
                                ? 'bg-industrial-success/30 text-industrial-success'
                                : 'bg-industrial-bg text-industrial-text-secondary'
                            }`}
                          >
                            M03 CW
                          </button>
                          <button
                            onClick={() => updateOpSetting(op.id, 'spindleDir', 'M04')}
                            data-testid={`spindle-ccw-${op.id}`}
                            className={`flex-1 py-1.5 text-xs font-mono font-medium transition-colors ${
                              op.spindleDir === 'M04'
                                ? 'bg-industrial-warning/30 text-industrial-warning'
                                : 'bg-industrial-bg text-industrial-text-secondary'
                            }`}
                          >
                            M04 CCW
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Edge Break Options (turning ops only) */}
                    {type === 'turning' && !op.isLiveTool && ['facing', 'od-roughing', 'od-finishing', 'id-boring', 'taper'].includes(op.type) && (
                      <div className="mb-3 p-2.5 bg-industrial-bg/60 border border-industrial-border rounded-lg">
                        <label className="text-xs text-industrial-text-secondary font-medium mb-2 block">Edge Break (Chamfer / Radius)</label>
                        <div className="grid grid-cols-2 gap-3">
                          {/* Entry Edge */}
                          <div>
                            <label className="text-[10px] text-industrial-text-secondary mb-1 block">Entry Edge</label>
                            <select
                              value={op.edgeBreak?.entry?.type || 'none'}
                              onChange={(e) => updateOpSetting(op.id, 'edgeBreak', {
                                ...op.edgeBreak,
                                entry: { ...op.edgeBreak?.entry, type: e.target.value }
                              })}
                              data-testid={`edge-entry-type-${op.id}`}
                              className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text mb-1"
                            >
                              <option value="none">None</option>
                              <option value="chamfer">Chamfer</option>
                              <option value="radius">Radius</option>
                            </select>
                            {op.edgeBreak?.entry?.type !== 'none' && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  step="0.001"
                                  value={op.edgeBreak?.entry?.size || 0.015}
                                  onChange={(e) => updateOpSetting(op.id, 'edgeBreak', {
                                    ...op.edgeBreak,
                                    entry: { ...op.edgeBreak?.entry, size: parseFloat(e.target.value) || 0.015 }
                                  })}
                                  data-testid={`edge-entry-size-${op.id}`}
                                  className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text font-mono"
                                />
                                <span className="text-[10px] text-industrial-text-secondary whitespace-nowrap">in</span>
                              </div>
                            )}
                          </div>
                          {/* Exit Edge */}
                          <div>
                            <label className="text-[10px] text-industrial-text-secondary mb-1 block">Exit Edge</label>
                            <select
                              value={op.edgeBreak?.exit?.type || 'none'}
                              onChange={(e) => updateOpSetting(op.id, 'edgeBreak', {
                                ...op.edgeBreak,
                                exit: { ...op.edgeBreak?.exit, type: e.target.value }
                              })}
                              data-testid={`edge-exit-type-${op.id}`}
                              className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text mb-1"
                            >
                              <option value="none">None</option>
                              <option value="chamfer">Chamfer</option>
                              <option value="radius">Radius</option>
                            </select>
                            {op.edgeBreak?.exit?.type !== 'none' && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  step="0.001"
                                  value={op.edgeBreak?.exit?.size || 0.015}
                                  onChange={(e) => updateOpSetting(op.id, 'edgeBreak', {
                                    ...op.edgeBreak,
                                    exit: { ...op.edgeBreak?.exit, size: parseFloat(e.target.value) || 0.015 }
                                  })}
                                  data-testid={`edge-exit-size-${op.id}`}
                                  className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text font-mono"
                                />
                                <span className="text-[10px] text-industrial-text-secondary whitespace-nowrap">in</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Profile Steps Builder (OD Roughing & ID Boring) */}
                    {op.profileSteps !== null && (op.type === 'od-roughing' || op.type === 'id-boring') && (
                      <div className="mb-3 p-2.5 bg-industrial-bg/60 border border-industrial-border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs text-industrial-text-secondary font-medium">
                            Profile Steps {op.profileSteps.length > 0 ? `(${op.profileSteps.length} steps)` : ''}
                          </label>
                          <button
                            onClick={() => {
                              const lastStep = op.profileSteps.length > 0
                                ? op.profileSteps[op.profileSteps.length - 1]
                                : null;
                              const newDia = lastStep
                                ? (op.type === 'od-roughing' ? lastStep.diameter + 0.25 : lastStep.diameter - 0.25)
                                : (op.params.finishDia || 1.5);
                              updateOpSetting(op.id, 'profileSteps', [
                                ...op.profileSteps,
                                { diameter: parseFloat(newDia.toFixed(4)), length: 0.5 }
                              ]);
                            }}
                            data-testid={`add-profile-step-${op.id}`}
                            className="px-2 py-1 text-[10px] bg-industrial-safety/20 border border-industrial-safety/30 rounded text-industrial-safety hover:bg-industrial-safety/30 transition-colors flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> Add Step
                          </button>
                        </div>
                        {op.profileSteps.length === 0 ? (
                          <p className="text-[10px] text-industrial-text-secondary text-center py-2">
                            No steps defined — using single straight {op.type === 'od-roughing' ? 'OD' : 'bore'} profile from parameters above.
                            Add steps for a stepped/contour profile.
                          </p>
                        ) : (
                          <div className="space-y-1.5">
                            {op.profileSteps.map((step, si) => (
                              <div key={si} className="flex items-center gap-2" data-testid={`profile-step-${op.id}-${si}`}>
                                <span className="text-[10px] text-industrial-text-secondary w-4 text-right font-mono">{si + 1}</span>
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-[10px] text-industrial-text-secondary">Dia (in)</label>
                                    <input
                                      type="number"
                                      step="0.001"
                                      value={step.diameter}
                                      onChange={(e) => {
                                        const newSteps = [...op.profileSteps];
                                        newSteps[si] = { ...newSteps[si], diameter: parseFloat(e.target.value) || 0 };
                                        updateOpSetting(op.id, 'profileSteps', newSteps);
                                      }}
                                      data-testid={`step-dia-${op.id}-${si}`}
                                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text font-mono"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-industrial-text-secondary">Length (in)</label>
                                    <input
                                      type="number"
                                      step="0.001"
                                      value={step.length}
                                      onChange={(e) => {
                                        const newSteps = [...op.profileSteps];
                                        newSteps[si] = { ...newSteps[si], length: parseFloat(e.target.value) || 0 };
                                        updateOpSetting(op.id, 'profileSteps', newSteps);
                                      }}
                                      data-testid={`step-len-${op.id}-${si}`}
                                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1 text-xs text-industrial-text font-mono"
                                    />
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    updateOpSetting(op.id, 'profileSteps', op.profileSteps.filter((_, i) => i !== si));
                                  }}
                                  data-testid={`remove-step-${op.id}-${si}`}
                                  className="p-1 rounded hover:bg-red-500/20"
                                >
                                  <Trash2 className="w-3 h-3 text-red-400" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Operation-Specific Parameters */}
                    <div className="grid grid-cols-2 gap-3">
                      {(op.isLiveTool ? liveToolingOperations[op.type] : operations[op.type])?.fields?.map(field => {
                        // Get thread list for threadSelect fields
                        const isThreadSelect = field.type === 'threadSelect';
                        const isSelect = field.type === 'select';
                        const threadList = isThreadSelect 
                          ? getThreadList(op.params.threadSystem || 'imperial', op.params.threadSeries || 'UNC')
                          : [];
                        
                        // Filter series options based on system
                        let selectOptions = field.options || [];
                        if (field.id === 'threadSeries') {
                          selectOptions = (op.params.threadSystem || 'imperial') === 'imperial' 
                            ? ['UNC', 'UNF'] 
                            : ['coarse', 'fine'];
                        }
                        
                        // Hide metric fields when imperial and vice versa
                        if (field.id === 'tpi' && (op.params.threadSystem === 'metric')) return null;
                        
                        // Read-only auto-populated fields
                        const isAutoField = ['majorDia', 'minorDia', 'pitch', 'tpi'].includes(field.id) 
                          && op.type === 'od-threading' && op.params.threadDesignation;
                        
                        return (
                          <div key={field.id} className={field.id === 'threadDesignation' ? 'col-span-2' : ''}>
                            <label className="text-xs text-industrial-text-secondary mb-1 block">
                              {field.label}
                              {isAutoField && <span className="text-industrial-accent ml-1">(auto)</span>}
                            </label>
                            {isSelect ? (
                              <select
                                value={op.params[field.id] ?? field.default}
                                onChange={(e) => updateOpParam(op.id, field.id, e.target.value)}
                                data-testid={`field-${field.id}`}
                                className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                              >
                                {selectOptions.map(opt => (
                                  <option key={opt} value={opt}>{opt.toUpperCase()}</option>
                                ))}
                              </select>
                            ) : isThreadSelect ? (
                              <select
                                value={op.params[field.id] ?? ''}
                                onChange={(e) => updateOpParam(op.id, field.id, e.target.value)}
                                data-testid={`field-${field.id}`}
                                className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                              >
                                <option value="">-- Select Thread --</option>
                                {threadList.map(t => (
                                  <option key={t.designation} value={t.designation}>
                                    {t.designation} {t.tpi ? `(${t.tpi} TPI)` : `(${t.pitch}mm pitch)`}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="number"
                                step="0.001"
                                value={op.params[field.id] ?? field.default}
                                onChange={(e) => updateOpParam(op.id, field.id, e.target.value)}
                                readOnly={isAutoField}
                                className={`w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono ${isAutoField ? 'opacity-70' : ''}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate Button */}
      <button
        onClick={generateProgram}
        disabled={programOps.filter(op => op.enabled).length === 0}
        data-testid="generate-program-btn"
        className={`w-full py-4 bg-${accentBg} text-${type === 'turning' ? 'black' : 'white'} font-heading font-bold uppercase tracking-wider rounded-lg shadow-button active:shadow-none active:translate-y-1 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <Play className="w-5 h-5" />
        Generate Complete Program
      </button>

      {/* G-Code Output */}
      {gcode.length > 0 && (
        <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden" data-testid="gcode-output">
          <div className="flex items-center justify-between px-4 py-2 bg-industrial-surface-highlight border-b border-industrial-border">
            <span className="text-sm font-medium text-industrial-text">
              Generated Program ({gcode.length} lines)
            </span>
            <div className="flex gap-2">
              <button
                onClick={saveProgram}
                disabled={saving || saved}
                data-testid="save-program-btn"
                className="p-2 rounded hover:bg-industrial-border transition-colors disabled:opacity-50"
                title="Save to Library"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 text-industrial-text-secondary animate-spin" />
                ) : saved ? (
                  <CheckCircle className="w-4 h-4 text-industrial-success" />
                ) : (
                  <Save className="w-4 h-4 text-industrial-text-secondary" />
                )}
              </button>
              <button
                onClick={() => setShowToolpathViewer(!showToolpathViewer)}
                data-testid="toggle-toolpath-btn"
                className="p-2 rounded hover:bg-industrial-border transition-colors"
                title={showToolpathViewer ? "Hide Toolpath" : "Show Toolpath"}
              >
                {showToolpathViewer ? (
                  <Eye className="w-4 h-4 text-industrial-safety" />
                ) : (
                  <EyeOff className="w-4 h-4 text-industrial-text-secondary" />
                )}
              </button>
              <button
                onClick={copyCode}
                data-testid="copy-program-btn"
                className="p-2 rounded hover:bg-industrial-border transition-colors"
                title="Copy to Clipboard"
              >
                <Copy className={`w-4 h-4 ${copied ? 'text-industrial-success' : 'text-industrial-text-secondary'}`} />
              </button>
              <button
                onClick={downloadCode}
                data-testid="download-program-btn"
                className="p-2 rounded hover:bg-industrial-border transition-colors"
                title="Download .nc file"
              >
                <Download className="w-4 h-4 text-industrial-text-secondary" />
              </button>
            </div>
          </div>
          
          {/* 2D/3D Toolpath Visualization */}
          {showToolpathViewer && (
            <div className="border-b border-industrial-border">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 px-4 py-2 bg-industrial-surface-highlight border-b border-industrial-border">
                <span className="text-xs text-industrial-text-secondary mr-2">View:</span>
                <button
                  onClick={() => setViewMode('2d')}
                  data-testid="view-mode-2d"
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    viewMode === '2d' ? 'bg-industrial-safety/20 text-industrial-safety' : 'text-industrial-text-secondary hover:text-industrial-text'
                  }`}
                >
                  2D
                </button>
                <button
                  onClick={() => setViewMode('3d')}
                  data-testid="view-mode-3d"
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    viewMode === '3d' ? 'bg-cyan-500/20 text-cyan-400' : 'text-industrial-text-secondary hover:text-industrial-text'
                  }`}
                >
                  3D
                </button>
              </div>
              
              {viewMode === '2d' ? (
                <div className="p-4">
                  <ToolpathViewer
                    gcode={gcode.join('\n')}
                    width={Math.min(window.innerWidth - 80, 600)}
                    height={250}
                    machineType={type}
                    showRapids={true}
                    showGrid={true}
                    showWorkpiece={true}
                    stockDimensions={stockDimensions}
                    className="mx-auto"
                  />
                </div>
              ) : (
                <ToolpathViewer3D
                  gcode={gcode.join('\n')}
                  machineType={type}
                  stockDimensions={stockDimensions}
                  showRapids={true}
                  showWorkpiece={true}
                  height={350}
                />
              )}
            </div>
          )}
          
          <div className="p-4 max-h-96 overflow-y-auto bg-[#1e1e1e]">
            <pre className="font-mono text-sm leading-relaxed">
              {gcode.map((line, i) => (
                <div 
                  key={i} 
                  className="flex"
                  dangerouslySetInnerHTML={{ 
                    __html: `<span class="gcode-line-number w-10 inline-block text-right pr-3">${String(i + 1).padStart(4)}</span>${formatGCodeLine(line)}`
                  }}
                />
              ))}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramBuilder;
